import React from 'react';
import { motion } from 'framer-motion';
import { 
  GraduationCap, 
  Shield, 
  Lock, 
  Users, 
  Database, 
  Cloud,
  Book,
  Network,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

interface EducationProps {
  setCurrentPage: (page: string) => void;
}

const Education: React.FC<EducationProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Shield,
      title: "Campus Security",
      description: "Comprehensive security solutions for educational institutions and campuses.",
      items: [
        "Network security infrastructure",
        "Physical security integration",
        "Access control systems",
        "Emergency response protocols"
      ]
    },
    {
      icon: Lock,
      title: "Data Protection",
      description: "Secure student and institutional data with advanced protection measures.",
      items: [
        "FERPA compliance",
        "Student data protection",
        "Research data security",
        "Privacy management"
      ]
    },
    {
      icon: Cloud,
      title: "Digital Learning Security",
      description: "Secure online learning environments and digital resources.",
      items: [
        "E-learning platform security",
        "Remote access protection",
        "Content security",
        "Authentication systems"
      ]
    },
    {
      icon: Network,
      title: "Infrastructure Protection",
      description: "Robust security for educational technology infrastructure.",
      items: [
        "Network monitoring",
        "Device management",
        "Wireless security",
        "IoT device protection"
      ]
    }
  ];

  const challenges = [
    {
      icon: Users,
      title: "User Management",
      items: [
        "Student access control",
        "Faculty permissions",
        "Guest network security",
        "BYOD policies"
      ]
    },
    {
      icon: Database,
      title: "Data Management",
      items: [
        "Academic records security",
        "Research data protection",
        "Administrative data",
        "Compliance requirements"
      ]
    },
    {
      icon: Book,
      title: "Resource Security",
      items: [
        "Digital library protection",
        "Learning material security",
        "Intellectual property",
        "Content access control"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <GraduationCap className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Education Security Solutions</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Comprehensive cybersecurity solutions designed specifically for educational institutions, protecting digital learning environments and sensitive academic data
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Challenges Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Industry Challenges</h2>
            <p className="text-lg text-muted">
              Addressing unique security challenges in educational environments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {challenges.map((challenge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <challenge.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{challenge.title}</h3>
                </div>
                
                <ul className="space-y-3">
                  {challenge.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Secure Your Educational Institution</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our education security experts to discuss your institution's specific needs and discover how we can help protect your digital learning environment
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Education;